// eslint-disable-next-line max-classes-per-file
import React from 'react';
import PropTypes from 'prop-types';
import { fromGlobalId } from 'graphql-relay';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';

import get from 'lodash/get';
import map from 'lodash/map';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import toUpper from 'lodash/toUpper';

import { formatLocationByPath } from '../../utils/location-utils';

import Icon from '../../components/Icon';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import Header from '../../components/Header';
import FairDetails from '../../components/FairDetails';
import HighSchoolDetailDisplay from '../HighSchoolDetailsDisplay';
import FairRequest from '../FairRequest';
import { getFairAvailabilityAction } from '../../utils/fair-utils';

import './styles.css';
import * as constants from '../Authorization/constants';
import { onHEAccountSubscription } from '../../utils/subscriptions';

const messages = defineMessages({
  fairsAvailabilityNone: {
    id: 'fairs.availability.none',
    defaultMessage: 'No additional upcoming fairs you may be interested in.',
  },
  fairsAvailabilityMore: {
    id: 'fairs.availability.more',
    defaultMessage: 'More upcoming fairs you may be interested in.',
  },
  fairsAvailabilityMoreCollegeFair: {
    id: 'fairs.availability.more.fair',
    defaultMessage: 'College Fair',
  },
  fairsAvailabilityMoreHighSchool: {
    id: 'fairs.availability.more.highshcool',
    defaultMessage: 'High School',
  },
  fairsAvailabilityMoreLocation: {
    id: 'fairs.availability.more.location',
    defaultMessage: 'Location',
  },
  fairsAvailabilityMoreDistance: {
    id: 'fairs.availability.more.distance',
    defaultMessage: 'Distance',
  },
  fairsAvailabilityMoreDateTime: {
    id: 'fairs.availability.more.datetime',
    defaultMessage: 'Date/Time',
  },
  fairsAvailabilityMoreDay: {
    id: 'fairs.availability.more.day',
    defaultMessage: 'Day of Week',
  },
});

const MAX_UPCOMING_FAIRS = 25;
const COLUMN_SORT_ASCENDING = 'ascending';
const COLUMN_SORT_DESCENDING = 'descending';

const COLUMN_SCHOOL_NAME = 'schoolName';
const COLUMN_FAIR_NAME = 'fairName';
const COLUMN_LOCATION = 'location';
const COLUMN_DISTANCE = 'distance';
const COLUMN_DATE_AND_TIME = 'dateAndTime';

export class HostedCollegeFair {
  constructor(id, schoolName, phone, address, distance = 'N/A', primaryContact, collegeFairNode, intl) {
    this.intl = intl;
    this.id = id;
    this.phone = phone;
    this.address = address;
    this.distance = distance;
    this.schoolName = schoolName;
    this.primaryContact = primaryContact;
    this.fairName = collegeFairNode.name;
    this.collegeFairNode = collegeFairNode;

    this.isoStartTime = this.getIsoStartTime();
    this.formattedLocation = this.getFormattedLocation();
    this.formattedDateAndTime = this.getFormatDateAndTime();
  }

  getFormattedLocation() {
    if (!this.formattedLocation) {
      this.formattedLocation = formatLocationByPath(this.address, 'field_hs_elna_city', 'field_hs_elna_state_latest');
    }
    return this.formattedLocation;
  }

  getIsoStartTime() {
    if (!this.isoStartTime) {
      this.isoStartTime = `${this.collegeFairNode.date} - ${this.collegeFairNode.startISOTime}`;
    }
    return this.isoStartTime;
  }

  getFormatDateAndTime() {
    if (!this.formattedDateAndTime) {
      const formattedDate = this.intl.formatDate(`${this.collegeFairNode.date}T12:00:00`, {
        day: 'numeric', month: 'numeric', year: 'numeric',
      });

      this.formattedDateAndTime = `${formattedDate} ${toUpper(this.collegeFairNode.startTime)}`;
    }

    return this.formattedDateAndTime;
  }
}

function getHostedCollegeFairs(highSchoolsWithCollegeFairsWithinProximity, intl, maxUpcomingFairs) {
  /* istanbul ignore if */
  if (!highSchoolsWithCollegeFairsWithinProximity || !highSchoolsWithCollegeFairsWithinProximity.length) {
    return {};
  }
  const selectedResult = highSchoolsWithCollegeFairsWithinProximity[0];
  const selectedHighSchool = get(selectedResult, 'toSchool');

  const collegeFairs = highSchoolsWithCollegeFairsWithinProximity.reduce((accumulated, repVisitHighSchoolWithinProximityResult) => {
    const repVisitHighSchoolWithinProximity = get(repVisitHighSchoolWithinProximityResult, 'toSchool');
    /* istanbul ignore if */
    if (accumulated.length >= maxUpcomingFairs || selectedHighSchool.id === repVisitHighSchoolWithinProximity.id) {
      return accumulated;
    }

    const primaryContact = get(selectedHighSchool, 'repVisitsAccount.collegeFairsPrimaryContact');
    const highSchoolPhone = get(repVisitHighSchoolWithinProximity, 'naviance.field_hs_elna_phone');
    const highSchoolAddress = get(repVisitHighSchoolWithinProximity, 'address');
    const highSchoolId = fromGlobalId(repVisitHighSchoolWithinProximity.id).id;
    const highSchoolName = get(repVisitHighSchoolWithinProximity, 'name');
    const distance = get(repVisitHighSchoolWithinProximityResult, 'distance.text', 'N/A');

    repVisitHighSchoolWithinProximity.collegeFairs.edges.some((filteredCollegeFair) => {
      accumulated = [
        ...accumulated,
        new HostedCollegeFair(
          highSchoolId,
          highSchoolName,
          highSchoolPhone,
          highSchoolAddress,
          distance,
          primaryContact,
          filteredCollegeFair.node,
          intl,
        ),
      ];
      if (accumulated.length >= maxUpcomingFairs) {
        return true;
      }
      return false;
    });

    return accumulated;
  }, []);

  return { selectedHighSchool, collegeFairs };
}

function sortByColumn(column, order, collegeFairs) {
  order = order.slice(0, 4);
  switch (column) {
    case COLUMN_FAIR_NAME:
      collegeFairs = sortBy(collegeFairs, ['fairName'], [order]);
      break;
    case COLUMN_SCHOOL_NAME:
      collegeFairs = sortBy(collegeFairs, ['schoolName'], [order]);
      break;
    case COLUMN_LOCATION:
      collegeFairs = sortBy(collegeFairs, ['formattedLocation'], [order]);
      break;
    case COLUMN_DISTANCE:
      collegeFairs = sortBy(collegeFairs, ['distance'], [order]);
      break;
    case COLUMN_DATE_AND_TIME:
      collegeFairs = sortBy(collegeFairs, ['isoStartTime'], [order]);
      break;
    default:
      collegeFairs = sortBy(collegeFairs, ['id'], [order]);
      break;
  }

  return collegeFairs;
}

export const FreemiumOrHubsSubscription = onHEAccountSubscription(
  (heAccount) => !heAccount.freemium && some(heAccount.activeSubscriptions, (s) => s !== constants.HUBS_SUBSCRIPTION),
  { failure: ({ title }) => <span color="black">{title}</span> }, /* eslint-disable-line react/prop-types */
)(({ title, onClick }) => <Link onClick={onClick}>{title}</Link>); /* eslint-disable-line jsx-a11y/anchor-is-valid */

class UpcomingFairsAvailability extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatDate: PropTypes.func.isRequired,
    }).isRequired,
    viewer: PropTypes.object,
    maxUpcomingFairs: PropTypes.number,
  };

  static defaultProps = {
    maxUpcomingFairs: MAX_UPCOMING_FAIRS,
  };

  constructor(props) {
    super(props);
    const { intl, viewer, maxUpcomingFairs } = this.props;
    const { highSchoolsWithCollegeFairsWithinProximity } = viewer;
    const hostedCollegeFairs = getHostedCollegeFairs(highSchoolsWithCollegeFairsWithinProximity, intl, maxUpcomingFairs);

    this.state = {
      column: null,
      direction: COLUMN_SORT_DESCENDING,
      collegeFairs: hostedCollegeFairs.collegeFairs,
      detailsModalOpen: false,
      scheduleModalOpen: false,
      openedHighSchoolFields: undefined,
      openedCollegeFair: undefined,
    };
  }

  /* istanbul ignore next */
  componentWillReceiveProps(nextProps) {
    const { intl, viewer, maxUpcomingFairs } = nextProps;
    const { highSchoolsWithCollegeFairsWithinProximity } = viewer;
    const hostedCollegeFairs = getHostedCollegeFairs(highSchoolsWithCollegeFairsWithinProximity, intl, maxUpcomingFairs);

    const { column, direction } = this.state;
    const sortedCollegeFairs = sortByColumn(column, direction, hostedCollegeFairs.collegeFairs);

    this.setState({
      collegeFairs: sortedCollegeFairs,
    });
  }

  openDetailsModal = (openedHighSchoolFields, openedCollegeFair) => {
    const highSchoolDetailsParams = { id: openedHighSchoolFields.id };
    this.setState({ detailsModalOpen: true, openedHighSchoolFields, openedCollegeFair, highSchoolDetailsParams });
  };

  closeDetailsModal = () => { this.setState({ detailsModalOpen: false }); };

  toggleScheduleModal = () => { this.setState((prevState) => ({ scheduleModalOpen: !prevState.scheduleModalOpen })); };

  _handleSort = (clickedColumn) => () => {
    let { column, collegeFairs, direction } = this.state;

    if (clickedColumn === column) {
      direction = (direction === COLUMN_SORT_ASCENDING) ? COLUMN_SORT_DESCENDING : COLUMN_SORT_ASCENDING;
      collegeFairs = collegeFairs.reverse();
    } else {
      column = clickedColumn;
      collegeFairs = sortByColumn(column, direction, collegeFairs);
    }

    this.setState({
      column,
      direction,
      collegeFairs,
    });
  };

  detailsAction = (collegeFair) => {
    const fairAction = getFairAvailabilityAction(collegeFair);
    const { label } = fairAction;
    const action = fairAction.disabled ? undefined : () => { this.toggleScheduleModal(); this.closeDetailsModal(); };
    return ({ label, onClick: action });
  };

  render() {
    const {
      collegeFairs,
      column,
      direction,
      openedHighSchoolFields,
      highSchoolDetailsParams,
      openedCollegeFair,
      scheduleModalOpen,
      detailsModalOpen,
    } = this.state;

    const { viewer } = this.props;

    if (!collegeFairs || collegeFairs.length === 0) {
      return (null);
    }

    return (
      <div>
        {openedHighSchoolFields && openedCollegeFair && (
        <div>
          <FairRequest
            highSchoolName={openedHighSchoolFields.schoolName}
            fairNode={openedCollegeFair}
            viewer={viewer}
            open={scheduleModalOpen}
            onClose={this.toggleScheduleModal}
          />
          <Modal
            basic
            styleName="details-modal"
            closeIcon={<Icon name="close" styleName="details-modal-close" />}
            onClose={this.closeDetailsModal}
            open={detailsModalOpen}
          >
            <HighSchoolDetailDisplay
              {...highSchoolDetailsParams}
              contactType="fairs"
              detailsAction={this.detailsAction(openedCollegeFair)}
            >
              <Header as="h2" styleName="details-header">
                {openedCollegeFair.name}
              </Header>
              <div styleName="details-body">
                <FairDetails
                  schoolPhone={openedHighSchoolFields.phone}
                  collegeFair={openedCollegeFair}
                  primaryContact={openedHighSchoolFields.primaryContact}
                  schoolAddress={{
                    city: openedHighSchoolFields.address.field_hs_elna_city,
                    state: openedHighSchoolFields.address.field_hs_elna_state_latest,
                    street: openedHighSchoolFields.address.field_hs_elna_address_line1,
                  }}
                />
              </div>
            </HighSchoolDetailDisplay>
          </Modal>
        </div>
        )}
        <div styleName="fairs-availability-more-title">
          <FormattedMessage {...messages.fairsAvailabilityMore} />
        </div>
        <Table sortable celled compact>
          <caption styleName="hidden">
            <FormattedMessage {...messages.fairsAvailabilityMore} />
          </caption>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell sorted={column === COLUMN_SCHOOL_NAME && direction} onClick={this._handleSort(COLUMN_SCHOOL_NAME)}>
                <FormattedMessage {...messages.fairsAvailabilityMoreHighSchool} />
              </Table.HeaderCell>
              <Table.HeaderCell sorted={column === COLUMN_FAIR_NAME && direction} onClick={this._handleSort(COLUMN_FAIR_NAME)}>
                <FormattedMessage {...messages.fairsAvailabilityMoreCollegeFair} />
              </Table.HeaderCell>
              <Table.HeaderCell sorted={column === COLUMN_LOCATION && direction} onClick={this._handleSort(COLUMN_LOCATION)}>
                <FormattedMessage {...messages.fairsAvailabilityMoreLocation} />
              </Table.HeaderCell>
              <Table.HeaderCell sorted={column === COLUMN_DISTANCE && direction} onClick={this._handleSort(COLUMN_DISTANCE)}>
                <FormattedMessage {...messages.fairsAvailabilityMoreDistance} />
              </Table.HeaderCell>
              <Table.HeaderCell sorted={column === COLUMN_DATE_AND_TIME && direction} onClick={this._handleSort(COLUMN_DATE_AND_TIME)}>
                <FormattedMessage {...messages.fairsAvailabilityMoreDateTime} />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map(collegeFairs, ({
              id,
              fairName,
              schoolName,
              phone,
              address,
              distance,
              primaryContact,
              collegeFairNode,
              formattedLocation,
              formattedDateAndTime,
            }) => (
              <Table.Row key={collegeFairNode.id}>
                <Table.Cell>
                  <FreemiumOrHubsSubscription
                    title={schoolName}
                    onClick={() => this.openDetailsModal(
                      { id, schoolName, phone, address, distance, primaryContact }, collegeFairNode,
                    )}
                  />
                </Table.Cell>
                <Table.Cell>
                  {fairName}
                </Table.Cell>
                <Table.Cell>
                  {formattedLocation}
                </Table.Cell>
                <Table.Cell>
                  {distance}
                </Table.Cell>
                <Table.Cell>
                  {formattedDateAndTime}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export default injectIntl(UpcomingFairsAvailability);
